import React, {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useMemo,
    useReducer,
    useRef,
} from 'react';
import {
    DOC_TYPE_DOWNLOADABLE,
    DOC_TYPE_REQUIRED,
    type Devis,
    type FactureFormalite,
    type NestedDossierFormaliteOpenSearch,
    type NestedDossierProcessesOpenSearch,
    useAnnoncesLegalesService,
    useFacturationService,
    useFormaliteService,
    useSociete,
} from '@europrocurement/l2d-domain';
import { castIriToId, getFileExtension } from '@europrocurement/l2d-utils';
import { VALIDATOR_TYPE_LEGAL_ANNOUCEMENT } from '@europrocurement/l2d-modules/modules/Dossiers/constants';
import useBillingActions from '../modules/Dossiers/hooks/useBillingActions';
import useBillingRender from '../modules/Dossiers/hooks/useBillingRender';
import type { DocumentExtension } from '../modules/Dossiers/hooks/useDossierDocumentFilesApi';
import useDocumentSign from '../modules/Dossiers/hooks/useDocumentSign';
import { type DisplayedDocument } from '../modules/Dossiers/providers/types';
import {
    collectionInvoiceApiToStateTransformer,
    collectionEstimateApiToStateTransformer,
} from './CollectionBillingProvider';
import useDossierDocumentFilesApi from '../modules/Dossiers/hooks/useDossierDocumentFilesApi';
import { useDownload } from '../hooks';
import { useDocumentPreviewContext } from '../modules/Dossiers/providers/DocumentPreviewContext';
// ---- Types ----

export type DriveDocumentCategory =
    | 'factures'
    | 'devis'
    | 'encaissements'
    | 'docs_requis'
    | 'docs_remis'
    | 'annonces_legales'
    | 'docs_signes';

export type DriveDocumentStateType = DisplayedDocument & {
    category: DriveDocumentCategory;
    showUploadBtn: boolean;
    onDownload: (downloadName: string, id: number) => Promise<void>;
    onShow: (showName: string, id: number) => Promise<void>;
    /** Returns a raw Blob — used for email attachments. Null when no file exists. */
    onGetBlob: () => Promise<Blob | null>;
};

/** Default DisplayedDocument boolean fields — all false for drive documents */
const BASE_DRIVE_DOC = {
    uploadable: false,
    uploading: false,
    editable: false,
    showable: true,
    signable: false,
    canDownloadSigned: false,
    downloadable: true,
    removable: false,
    removableDoc: false,
    movable: false,
    hasType: false,
    showStatus: true,
    showUploadBtn: false,
} as const;

/** Map an InvoiceStatus to the DisplayedDocument status field */
const toDocStatus = (invoiceStatus: 'paid' | 'pending' | 'draft'): 'pending' | 'success' =>
    invoiceStatus === 'paid' ? 'success' : 'pending';

type CategoryState = {
    loading: boolean;
    loaded: boolean;
    data: DriveDocumentStateType[];
};

type DriveDocumentsProviderState = Record<DriveDocumentCategory, CategoryState>;

// ---- Reducer ----

const EMPTY_CATEGORY: CategoryState = { loading: false, loaded: false, data: [] };
const SET_LOADING = 'set_drive_doc_loader';
const SET_DATA = 'set_drive_doc_data';

const initialState: DriveDocumentsProviderState = {
    factures: EMPTY_CATEGORY,
    devis: EMPTY_CATEGORY,
    encaissements: EMPTY_CATEGORY,
    docs_requis: EMPTY_CATEGORY,
    docs_remis: EMPTY_CATEGORY,
    annonces_legales: EMPTY_CATEGORY,
    docs_signes: EMPTY_CATEGORY,
};

const RESET = 'reset_drive_docs';

type Action =
    | { type: 'set_drive_doc_loader'; category: DriveDocumentCategory }
    | {
          type: 'set_drive_doc_data';
          category: DriveDocumentCategory;
          data: DriveDocumentStateType[];
      }
    | { type: 'reset_drive_docs' };

function driveDocumentsReducer(
    state: DriveDocumentsProviderState,
    action: Action,
): DriveDocumentsProviderState {
    switch (action.type) {
        case SET_LOADING:
            return {
                ...state,
                [action.category]: { ...state[action.category], loading: true },
            };
        case SET_DATA:
            return {
                ...state,
                [action.category]: { loading: false, loaded: true, data: action.data },
            };
        case RESET:
            return initialState;
        default:
            throw new Error(`Unknown action: ${(action as Action).type}`);
    }
}

// ---- Context ----

export type LoadCategoriesParams = {
    dossiersIds: number[];
    collectionIds: number[];
    categories: DriveDocumentCategory[];
};

type DriveDocumentsContextValue = DriveDocumentsProviderState & {
    loadCategories: (params: LoadCategoriesParams) => Promise<void>;
};

const DriveDocumentsContext = createContext<DriveDocumentsContextValue | null>(null);

// ---- Provider ----

export type DriveDocumentsProviderProps = PropsWithChildren;

const STATUS_VALID = 9;
const STATUS_REFUSED = 10;
const TYPE_NO_TYPE = 66;
const TYPE_LIASSE = 118;

const DriveDocumentsProvider: React.FunctionComponent<DriveDocumentsProviderProps> = function ({
    children,
}) {
    const [state, dispatch] = useReducer(driveDocumentsReducer, initialState);

    // Prevents concurrent fetches for the same category
    const inFlightRef = useRef<Set<DriveDocumentCategory>>(new Set());

    // Tracks the IDs used for the last load — state is reset when they change
    const loadedForRef = useRef<{ dossiersIds: number[]; collectionIds: number[] } | null>(null);

    // Services — direct API calls, no CollectionBillingProvider dependency
    const { factureApi, devisFormaliteApi } = useFacturationService();
    const { reglementsApi, documentGedApi, dossierOpenSearchApi, dossierSignApi } =
        useFormaliteService();
    const { legalPrestationsApi } = useAnnoncesLegalesService();
    const xIdSociete = useSociete();
    const { download } = useDownload();
    const { setDocumentPreview } = useDocumentPreviewContext();
    const { getDosDocFileContent, getDosDocs } = useDossierDocumentFilesApi();

    const { handleDownloadInvoice, handleShowInvoice, handleDownloadEstimate, handleShowEstimate } =
        useBillingActions();
    const { statusFromInvoice, feedbackFromInvoice, getInvoiceLabel, getEstimateLabel } =
        useBillingRender();
    const { getDossierSignMetadata, getDossiers, downloadDocument, handleShowDocument } =
        useDocumentSign();

    // ---- Per-category loaders ----

    const loadSignedDocs = useCallback(
        async (dossiersIds: number[]): Promise<void> => {
            if (
                state.docs_signes.loaded ||
                inFlightRef.current.has('docs_signes') ||
                !dossiersIds.length
            ) {
                return;
            }
            inFlightRef.current.add('docs_signes');
            dispatch({ type: SET_LOADING, category: 'docs_signes' });
            try {
                const allSignDossiers = (
                    await Promise.all(
                        dossiersIds.map(async (dossierId) => {
                            const metadata = await getDossierSignMetadata(dossierId);
                            return getDossiers(metadata);
                        }),
                    )
                ).flat();

                const processedFileIds = new Set<number>();
                const data: DriveDocumentStateType[] = allSignDossiers.flatMap((signDossier) =>
                    signDossier.documents
                        .filter((doc) => {
                            if (!doc.signed || doc.signedFileId == null) return false;
                            if (processedFileIds.has(doc.fileId)) return false;
                            processedFileIds.add(doc.fileId);
                            return true;
                        })
                        .map((doc) => ({
                            ...BASE_DRIVE_DOC,
                            id: doc.signedFileId as number,
                            dossierId: 0,
                            fichierId: doc.fileId,
                            documentTypeId: doc.signedFileId as number,
                            category: 'docs_signes' as DriveDocumentCategory,
                            label: `${doc.name} - Signé`,
                            fileName: doc.fileName,
                            type: doc.fileName
                                ? (getFileExtension(doc.fileName) as DocumentExtension)
                                : undefined,
                            status: (doc.signed ? 'success' : 'pending') as 'success' | 'pending',
                            feedback: (doc.signed ? 'Validé' : 'En attente') as
                                | 'Validé'
                                | 'En attente',
                            onDownload: downloadDocument,
                            onShow: handleShowDocument,
                            onGetBlob: async () => {
                                const response =
                                    await dossierSignApi.apiDossierDossiersSignFileIdGet(
                                        { id: doc.signedFileId as unknown as string },
                                        { responseType: 'blob' },
                                    );
                                return response.data as unknown as Blob;
                            },
                        })),
                );

                dispatch({ type: SET_DATA, category: 'docs_signes', data });
            } finally {
                inFlightRef.current.delete('docs_signes');
            }
        },
        [
            state.docs_signes.loaded,
            getDossierSignMetadata,
            getDossiers,
            downloadDocument,
            handleShowDocument,
            dossierSignApi,
        ],
    );

    const loadFactures = useCallback(
        async (collectionIds: number[]): Promise<void> => {
            if (
                state.factures.loaded ||
                inFlightRef.current.has('factures') ||
                !collectionIds.length
            ) {
                return;
            }
            inFlightRef.current.add('factures');
            dispatch({ type: SET_LOADING, category: 'factures' });
            try {
                const response = await factureApi.apiFacturesGetCollection({
                    xIdSociete,
                    collectionsId2: collectionIds,
                });
                const invoices = (response.data['hydra:member'] as FactureFormalite[]).map(
                    collectionInvoiceApiToStateTransformer,
                );
                dispatch({
                    type: SET_DATA,
                    category: 'factures',
                    data: invoices.map((invoice) => ({
                        ...BASE_DRIVE_DOC,
                        id: invoice.id,
                        dossierId: 0,
                        category: 'factures' as DriveDocumentCategory,
                        label: getInvoiceLabel(invoice),
                        status: toDocStatus(statusFromInvoice(invoice)),
                        feedback: feedbackFromInvoice(invoice),
                        onDownload: handleDownloadInvoice,
                        onShow: handleShowInvoice,
                        documentTypeId: invoice.id,
                        type: 'pdf',
                        onGetBlob: async () => {
                            const res = await factureApi.apiFacturesIdPdfGet(
                                { xIdSociete, id: invoice.id as unknown as string },
                                { responseType: 'blob' },
                            );
                            return res.data as unknown as Blob;
                        },
                    })),
                });
            } finally {
                inFlightRef.current.delete('factures');
            }
        },
        [
            state.factures.loaded,
            factureApi,
            xIdSociete,
            getInvoiceLabel,
            statusFromInvoice,
            feedbackFromInvoice,
            handleDownloadInvoice,
            handleShowInvoice,
        ],
    );

    const loadDevis = useCallback(
        async (collectionIds: number[]): Promise<void> => {
            if (state.devis.loaded || inFlightRef.current.has('devis') || !collectionIds.length) {
                return;
            }

            inFlightRef.current.add('devis');
            dispatch({ type: SET_LOADING, category: 'devis' });
            try {
                const response = await devisFormaliteApi.apiDevisFormaliteGetCollection({
                    xIdSociete,
                    collectionsId2: collectionIds,
                });

                const estimates = (response.data['hydra:member'] as Devis[]).map(
                    collectionEstimateApiToStateTransformer,
                );

                dispatch({
                    type: SET_DATA,
                    category: 'devis',
                    data: estimates
                        .filter((estimate) => estimate.active)
                        .map((estimate) => ({
                            ...BASE_DRIVE_DOC,
                            id: estimate.id,
                            dossierId: 0,
                            category: 'devis' as DriveDocumentCategory,
                            label: getEstimateLabel(estimate),
                            type: 'pdf',
                            status: 'success' as const,
                            documentTypeId: estimate.id,
                            feedback: 'Valide',
                            onDownload: handleDownloadEstimate,
                            onShow: handleShowEstimate,
                            onGetBlob: async () => {
                                const res = await devisFormaliteApi.apiDevisFormaliteIdPdfGet(
                                    { xIdSociete, id: estimate.id as unknown as string },
                                    { responseType: 'blob' },
                                );
                                return res.data as unknown as Blob;
                            },
                        })),
                });
            } finally {
                inFlightRef.current.delete('devis');
            }
        },
        [
            state.devis.loaded,
            devisFormaliteApi,
            xIdSociete,
            getEstimateLabel,
            handleDownloadEstimate,
            handleShowEstimate,
        ],
    );

    const loadEncaissements = useCallback(
        async (dossiersIds: number[]): Promise<void> => {
            if (
                state.encaissements.loaded ||
                inFlightRef.current.has('encaissements') ||
                !dossiersIds.length
            ) {
                return;
            }
            inFlightRef.current.add('encaissements');
            dispatch({ type: SET_LOADING, category: 'encaissements' });
            try {
                const response = await reglementsApi.apiReglementsDocumentsGetCollection({
                    dossierFormaliteId2: dossiersIds,
                    itemsPerPage: 100,
                });
                const gedDocs = response.data['hydra:member'];

                dispatch({
                    type: SET_DATA,
                    category: 'encaissements',
                    data: (gedDocs ?? [])
                        .filter((doc) => !doc.fgDelete && doc.idDocument)
                        .map((doc) => ({
                            ...BASE_DRIVE_DOC,
                            id: doc.idReglement ?? 0,
                            documentTypeId: doc.idReglement ?? 0,
                            dossierId: 0,
                            category: 'encaissements' as DriveDocumentCategory,
                            label: doc.filename || `Document GED ${doc.idDocument}`,
                            status: 'success' as const,
                            feedback: 'valide',
                            type: 'pdf',
                            onDownload: async (fileName: string) => {
                                const content =
                                    await documentGedApi.apiDocumentgedFileIdDocumentGet(
                                        { idDocument: doc.idDocument as string },
                                        { responseType: 'blob' },
                                    );
                                download(fileName, content.data as unknown as Blob);
                            },
                            onShow: async (fileName: string) => {
                                const content =
                                    await documentGedApi.apiDocumentgedFileIdDocumentGet(
                                        { idDocument: doc.idDocument as string },
                                        { responseType: 'blob' },
                                    );
                                setDocumentPreview({
                                    fileName,
                                    fileType: 'pdf',
                                    content: content.data as unknown as Blob,
                                });
                            },
                            onGetBlob: async () => {
                                const content =
                                    await documentGedApi.apiDocumentgedFileIdDocumentGet(
                                        { idDocument: doc.idDocument as string },
                                        { responseType: 'blob' },
                                    );
                                return content.data as unknown as Blob;
                            },
                        })),
                });
            } finally {
                inFlightRef.current.delete('encaissements');
            }
        },
        [state.encaissements.loaded, reglementsApi, documentGedApi, download, setDocumentPreview],
    );

    const loadLegalAnnoucementFiles = useCallback(
        async (collectionIds: number[]): Promise<void> => {
            if (
                state.annonces_legales.loaded ||
                inFlightRef.current.has('annonces_legales') ||
                !collectionIds.length
            ) {
                return;
            }
            inFlightRef.current.add('annonces_legales');
            dispatch({ type: SET_LOADING, category: 'annonces_legales' });
            try {
                const responseDossier =
                    await dossierOpenSearchApi.apiOpensearchDossiersGetCollection({
                        collectionId: collectionIds,
                    });

                const validatorEntries = responseDossier.data['hydra:member'].flatMap((doss) => {
                    const formalites =
                        (doss.formalites as unknown as NestedDossierFormaliteOpenSearch[]) ?? [];

                    return (
                        (
                            doss.validationProcesses as unknown as NestedDossierProcessesOpenSearch[]
                        )?.filter(
                            (vp) =>
                                vp.validatorTypeId === VALIDATOR_TYPE_LEGAL_ANNOUCEMENT &&
                                vp.identifier,
                        ) ?? []
                    ).map((validator) => {
                        const linkedFormalite = formalites.find((f) =>
                            f.articleIds.some((artId) => validator.articles.includes(artId)),
                        );
                        const label = linkedFormalite
                            ? `${linkedFormalite.libelle} - ${validator.identifier}`
                            : `${validator.validatorLabel ?? 'Annonce légale'} - ${validator.identifier}`;
                        return { validator, label };
                    });
                });

                dispatch({
                    type: SET_DATA,
                    category: 'annonces_legales',
                    data: validatorEntries.map(({ validator, label }) => ({
                        ...BASE_DRIVE_DOC,
                        id: validator.id,
                        dossierId: 0,
                        category: 'annonces_legales' as DriveDocumentCategory,
                        label,
                        type: 'pdf' as const,
                        status:
                            validator.statusCode === 'validate'
                                ? ('success' as const)
                                : ('pending' as const),
                        feedback: validator.statusLabel,
                        documentTypeId: validator.id,
                        onDownload: async (downloadName: string) => {
                            const content =
                                await legalPrestationsApi.getTblPrestaAttestationPrestationAnnonceLgaleItem(
                                    { id: validator.identifier, xIdSociete },
                                    { responseType: 'blob' },
                                );
                            download(downloadName, content.data as unknown as Blob);
                        },
                        onShow: async (showName: string) => {
                            const content =
                                await legalPrestationsApi.getTblPrestaAttestationPrestationAnnonceLgaleItem(
                                    { id: validator.identifier, xIdSociete },
                                    { responseType: 'blob' },
                                );
                            setDocumentPreview({
                                fileName: showName,
                                fileType: 'pdf',
                                content: content.data as unknown as Blob,
                            });
                        },
                        onGetBlob: async () => {
                            const content =
                                await legalPrestationsApi.getTblPrestaAttestationPrestationAnnonceLgaleItem(
                                    { id: validator.identifier, xIdSociete },
                                    { responseType: 'blob' },
                                );
                            return content.data as unknown as Blob;
                        },
                    })),
                });
            } finally {
                inFlightRef.current.delete('annonces_legales');
            }
        },
        [
            state.annonces_legales.loaded,
            dossierOpenSearchApi,
            legalPrestationsApi,
            xIdSociete,
            download,
            setDocumentPreview,
        ],
    );

    const loadDossierDocs = useCallback(
        async (
            dossiersIds: number[],
            category: Extract<DriveDocumentCategory, 'docs_requis' | 'docs_remis'>,
            docType: typeof DOC_TYPE_REQUIRED | typeof DOC_TYPE_DOWNLOADABLE,
        ): Promise<void> => {
            if (
                state[category].loaded ||
                inFlightRef.current.has(category) ||
                !dossiersIds.length
            ) {
                return;
            }
            inFlightRef.current.add(category);
            dispatch({ type: SET_LOADING, category });
            try {
                const responses = await Promise.all(
                    dossiersIds.map((dossierId) => getDosDocs(dossierId)),
                );
                const filtered = responses.flat().filter((doc) => {
                    if (!doc.fichiers || doc.fichiers.length === 0) return false;
                    const isNoType = [TYPE_NO_TYPE, TYPE_LIASSE].includes(
                        doc.document?.id as number,
                    );
                    if (!isNoType) {
                        return doc.document?.type === docType;
                    }
                    // No-type docs: use the file's expediteur to determine the category
                    return doc.fichiers.length === 1 && doc.fichiers[0].expediteur === docType;
                });
                dispatch({
                    type: SET_DATA,
                    category,
                    data: filtered.map((doc) => {
                        const fichier = doc.fichiers?.length === 1 ? doc.fichiers[0] : undefined;
                        const fichierId = fichier?.id as number | undefined;
                        const dossierId = castIriToId(doc.dossier as string);
                        const statusId =
                            fichier?.statut?.id ?? (doc.statut?.id as number | undefined);

                        let docStatus: 'pending' | 'error' | 'success' = 'pending';
                        if (fichier) {
                            if (statusId === STATUS_VALID) docStatus = 'success';
                            else if (statusId === STATUS_REFUSED) docStatus = 'error';
                        }

                        const rawLabel = doc.libelle ?? '';
                        const label = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1);

                        const rawFileName = fichier?.fichier as string | undefined;
                        const fileName = rawFileName ?? undefined;
                        const type = rawFileName
                            ? (getFileExtension(rawFileName) as DocumentExtension)
                            : undefined;

                        const noop = async () => Promise.resolve();

                        return {
                            ...BASE_DRIVE_DOC,
                            id: doc.id as number,
                            dossierId,
                            fichierId,
                            documentTypeId: doc.document?.id as number | undefined,
                            category,
                            label,
                            fileName,
                            type,
                            status: docStatus,
                            feedback: fichier?.statut?.libelle ? fichier.statut.libelle : undefined,
                            onDownload: fichierId
                                ? async (downloadName: string) => {
                                      const content = await getDosDocFileContent(fichierId);
                                      download(downloadName, content.data as unknown as Blob);
                                  }
                                : noop,
                            onShow: fichierId
                                ? async (showName: string) => {
                                      const content = await getDosDocFileContent(fichierId);
                                      setDocumentPreview({
                                          fileName: showName,
                                          fileType: 'pdf',
                                          content: content.data as unknown as Blob,
                                      });
                                  }
                                : noop,
                            onGetBlob: fichierId
                                ? async () => {
                                      const content = await getDosDocFileContent(fichierId);
                                      return content.data as unknown as Blob;
                                  }
                                : async () => null,
                        };
                    }),
                });
            } finally {
                inFlightRef.current.delete(category);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            state.docs_requis.loaded,
            state.docs_remis.loaded,
            getDosDocs,
            getDosDocFileContent,
            download,
            setDocumentPreview,
        ],
    );

    // Main load function
    const loadCategories = useCallback(
        async ({ dossiersIds, collectionIds, categories }: LoadCategoriesParams): Promise<void> => {
            const prev = loadedForRef.current;
            const idsChanged =
                !prev ||
                prev.dossiersIds.join(',') !== dossiersIds.join(',') ||
                prev.collectionIds.join(',') !== collectionIds.join(',');

            if (idsChanged) {
                inFlightRef.current.clear();
                dispatch({ type: RESET });
                loadedForRef.current = { dossiersIds, collectionIds };
            }

            await Promise.all(
                categories.map((cat) => {
                    switch (cat) {
                        case 'factures':
                            return loadFactures(collectionIds);
                        case 'devis':
                            return loadDevis(collectionIds);
                        case 'encaissements':
                            return loadEncaissements(dossiersIds);
                        case 'annonces_legales':
                            return loadLegalAnnoucementFiles(collectionIds);
                        case 'docs_requis':
                            return loadDossierDocs(dossiersIds, 'docs_requis', DOC_TYPE_REQUIRED);
                        case 'docs_remis':
                            return loadDossierDocs(
                                dossiersIds,
                                'docs_remis',
                                DOC_TYPE_DOWNLOADABLE,
                            );
                        case 'docs_signes':
                            return loadSignedDocs(dossiersIds);
                        default:
                            return Promise.resolve();
                    }
                }),
            );
        },
        [
            loadFactures,
            loadDevis,
            loadEncaissements,
            loadDossierDocs,
            loadLegalAnnoucementFiles,
            loadSignedDocs,
        ],
    );

    const contextValue = useMemo(() => ({ ...state, loadCategories }), [state, loadCategories]);

    return (
        <DriveDocumentsContext.Provider value={contextValue}>
            {children}
        </DriveDocumentsContext.Provider>
    );
};

// ---- Consumer hook ----

const useDriveDocumentsCtx = () => {
    const ctx = useContext(DriveDocumentsContext);
    if (!ctx) {
        throw new Error('useDriveDocumentsCtx must be used within a DriveDocumentsProvider');
    }

    const getCategoryData = (category: DriveDocumentCategory): DriveDocumentStateType[] =>
        ctx[category].data;

    const isCategoryLoaded = (category: DriveDocumentCategory): boolean => ctx[category].loaded;

    const isCategoryLoading = (category: DriveDocumentCategory): boolean => ctx[category].loading;

    const getAllDocuments = (): DriveDocumentStateType[] =>
        (Object.keys(ctx) as DriveDocumentCategory[])
            .filter((key): key is DriveDocumentCategory =>
                [
                    'factures',
                    'devis',
                    'encaissements',
                    'docs_requis',
                    'docs_remis',
                    'annonces_legales',
                    'docs_signes',
                ].includes(key),
            )
            .flatMap((cat) => getCategoryData(cat));

    return {
        loadCategories: ctx.loadCategories,
        getCategoryData,
        isCategoryLoaded,
        isCategoryLoading,
        getAllDocuments,
        ctx,

        // getters per category
        getFactures: () => getCategoryData('factures'),
        getDevis: () => getCategoryData('devis'),
        getEncaissements: () => getCategoryData('encaissements'),
        getDocsRequis: () => getCategoryData('docs_requis'),
        getDocsRemis: () => getCategoryData('docs_remis'),
        getAnnoncesLegales: () => getCategoryData('annonces_legales'),
        getDocsSignes: () => getCategoryData('docs_signes'),
    };
};

export { DriveDocumentsProvider, DriveDocumentsContext, useDriveDocumentsCtx };
